import React from 'react';
import { Star } from 'lucide-react';
import ContentForm from '../components/social-media-content/ContentForm';
import { useAuth } from '../contexts/AuthContext';

const SocialMediaContent = () => {
  const { currentUser } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto py-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center p-2 bg-indigo-100 rounded-full mb-4">
            <Star className="h-6 w-6 text-indigo-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Social Media Content Generator</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Create engaging, platform-optimized content for any social media platform in seconds
          </p>
        </div>

        {/* Main Content */}
        <ContentForm />

        {/* Features Section */}
        <div className="mt-16 max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">Why Use Our Content Generator?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-lg">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Save Time</h3>
              <p className="text-gray-600">
                Generate platform-optimized content in seconds instead of spending hours crafting the perfect post.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-lg">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Platform Optimized</h3>
              <p className="text-gray-600">
                Content tailored specifically for each platform's unique requirements and audience expectations.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-lg">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Customizable</h3>
              <p className="text-gray-600">
                Adjust tone, keywords, and content type to match your brand voice and marketing goals.
              </p>
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="mt-16 max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">How It Works</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold">1</div>
              <h3 className="font-medium text-gray-900 mb-2">Select Platform</h3>
              <p className="text-sm text-gray-600">Choose your target social media platform</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold">2</div>
              <h3 className="font-medium text-gray-900 mb-2">Enter Details</h3>
              <p className="text-sm text-gray-600">Provide your topic, tone, and keywords</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold">3</div>
              <h3 className="font-medium text-gray-900 mb-2">Generate Content</h3>
              <p className="text-sm text-gray-600">Our AI creates optimized content</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold">4</div>
              <h3 className="font-medium text-gray-900 mb-2">Copy & Post</h3>
              <p className="text-sm text-gray-600">Copy the content and post to your platform</p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Is the generated content unique?</h3>
              <p className="text-gray-600">
                Yes, each piece of content is freshly generated based on your specific inputs and requirements.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I edit the generated content?</h3>
              <p className="text-gray-600">
                Absolutely! The generated content is a starting point. We recommend reviewing and personalizing it before posting.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Which platforms are supported?</h3>
              <p className="text-gray-600">
                We support all major social media platforms including Instagram, Facebook, Twitter, LinkedIn, TikTok, and Pinterest.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Is there a limit to how many posts I can generate?</h3>
              <p className="text-gray-600">
                Currently, there are no limits on the number of posts you can generate with our tool.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialMediaContent;
